import type { PageListItem } from '@ongrid/shared';
import { formatNumber, formatPosition, getTrendBadgeClass, extractPath, truncate, calculateCTR } from '../lib/utils';

interface PageTableProps {
  data: PageListItem[];
  loading: boolean;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort: (column: string) => void;
  onRowClick: (pageUrl: string) => void;
}

export function PageTable({
  data,
  loading,
  sortBy,
  sortOrder,
  onSort,
  onRowClick,
}: PageTableProps) {
  const SortHeader = ({ column, label }: { column: string; label: string }) => {
    const isActive = sortBy === column;
    return (
      <th
        className="cursor-pointer hover:text-[var(--text-secondary)] transition-colors"
        onClick={() => onSort(column)}
      >
        <span className="flex items-center gap-1">
          {label}
          {isActive && (
            <span className="text-brand-500">
              {sortOrder === 'asc' ? '↑' : '↓'}
            </span>
          )}
        </span>
      </th>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-[var(--text-muted)]">Loading pages...</div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-[var(--text-muted)]">
        No pages found
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="data-table">
        <thead>
          <tr>
            <SortHeader column="page_url" label="Page" />
            <SortHeader column="last_30d_clicks" label="Clicks (30d)" />
            <SortHeader column="last_30d_impressions" label="Impressions" />
            <th>CTR</th>
            <SortHeader column="avg_position" label="Position" />
            <th>Trend</th>
            <SortHeader column="page_age_days" label="Age" />
          </tr>
        </thead>
        <tbody>
          {data.map((page) => (
            <tr
              key={page.page_url}
              className="cursor-pointer"
              onClick={() => onRowClick(page.page_url)}
            >
              <td className="max-w-md">
                <div className="flex flex-col">
                  <span className="font-medium text-[var(--text-primary)]">
                    {truncate(extractPath(page.page_url), 60)}
                  </span>
                  <span className="text-xs text-[var(--text-muted)] font-mono">
                    {truncate(page.page_url, 70)}
                  </span>
                </div>
              </td>
              <td className="font-mono">{formatNumber(page.last_30d_clicks)}</td>
              <td className="font-mono text-[var(--text-secondary)]">
                {formatNumber(page.last_30d_impressions)}
              </td>
              <td className="font-mono text-[var(--text-secondary)]">
                {calculateCTR(page.last_30d_clicks, page.last_30d_impressions)}
              </td>
              <td className="font-mono">{formatPosition(page.avg_position)}</td>
              <td>
                <span className={getTrendBadgeClass(page.trend_status)}>
                  {page.trend_status ?? 'Unknown'}
                </span>
              </td>
              <td className="text-[var(--text-secondary)]">
                {page.page_age_days !== null ? `${page.page_age_days}d` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

